/**
 * Select "Forma de pagamento" do bloco Condicoes comerciais (Tarefa F2).
 *
 * So aparece no motor DIVISOR (o caller decide). Lista as condicoes
 * cadastradas em Comercial → Configurações (favoritas primeiro); ao escolher,
 * PATCH /api/orcamentos/{id}/condicao-pagamento snapshota a taxa (s2) na
 * precificacao e re-aloca os precos — em seguida disparamos
 * "orc2:preco-invalidado" para o ComposicaoPrecoDivisor recarregar sozinho
 * (padrao dos editores do motor). O resumo das parcelas por evento aparece
 * embaixo; o texto livre da proposta continua no campo ao lado como
 * observacao de exibicao.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  listarCondicoesPagamento,
  resumirParcelasCondicao,
} from "../../services/financeiro";
import {
  getPrecoOrcamento,
  patchCondicaoPagamento,
} from "../../services/motorPreco";

export default function CondicaoPagamentoSelect({ orcamento, editavel }) {
  const { request } = useAuth();
  const toast = useToast();

  const [condicoes, setCondicoes] = useState(null); // null = carregando/sem acesso
  const [atualId, setAtualId] = useState("");
  const [taxaAtual, setTaxaAtual] = useState(null);
  const [congelado, setCongelado] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let ativo = true;
    // Sem permissao de leitura das condicoes (backend aceita orcamento:view),
    // o select simplesmente nao aparece — melhor esforco.
    listarCondicoesPagamento(request, { ativo: true }, { silentError: true })
      .then((payload) => {
        if (!ativo) return;
        const list = Array.isArray(payload) ? payload : payload?.results || [];
        setCondicoes(list);
      })
      .catch(() => { if (ativo) setCondicoes(null); });
    return () => { ativo = false; };
  }, [request]);

  const lerPreco = useCallback(async () => {
    try {
      const preco = await getPrecoOrcamento(
        request, orcamento.id, { silentError: true },
      );
      setAtualId(preco?.condicao_pagamento || "");
      setTaxaAtual(preco?.s?.taxa_pagamento_percentual ?? null);
      setCongelado(Boolean(preco?.congelado_em));
    } catch {
      // Precificacao ainda nao montada — select continua funcional.
    }
  }, [request, orcamento.id]);

  useEffect(() => { lerPreco(); }, [lerPreco]);

  const ordenadas = useMemo(
    () => (condicoes || []).slice().sort((a, b) =>
      (b.favorita === a.favorita
        ? a.nome.localeCompare(b.nome)
        : b.favorita ? 1 : -1)),
    [condicoes],
  );

  const selecionada = useMemo(
    () => ordenadas.find((c) => c.id === atualId) || null,
    [ordenadas, atualId],
  );

  const escolher = async (novoId) => {
    setSalvando(true);
    try {
      const preco = await patchCondicaoPagamento(
        request, orcamento.id, novoId || null,
      );
      setAtualId(preco?.condicao_pagamento || "");
      setTaxaAtual(preco?.s?.taxa_pagamento_percentual ?? null);
      // Padrao do motor: o painel de composicao recarrega sozinho.
      window.dispatchEvent(new CustomEvent("orc2:preco-invalidado"));
      toast.success(
        novoId
          ? "Forma de pagamento aplicada — taxa atualizada no motor."
          : "Forma de pagamento removida (taxa 0%).",
      );
    } catch {
      // O wrapper request ja mostra o toast de erro (detalhe do backend).
    } finally {
      setSalvando(false);
    }
  };

  if (condicoes === null) return null;

  return (
    <div className="cond-field" style={{ marginBottom: 10 }}>
      <label>
        Forma de pagamento (condição cadastrada)
        <select
          className="select"
          value={atualId}
          onChange={(e) => escolher(e.target.value)}
          disabled={!editavel || salvando || congelado}
        >
          <option value="">— sem condição (taxa 0%) —</option>
          {ordenadas.map((c) => (
            <option key={c.id} value={c.id}>
              {c.favorita ? "★ " : ""}{c.nome} · taxa {Number(c.taxa_pagamento_percentual || 0)}%
            </option>
          ))}
        </select>
      </label>
      {selecionada?.descricao && (
        <p className="cond-descricao">{selecionada.descricao}</p>
      )}
      {selecionada && (
        <p className="hint">
          {/* Sem parcelas, o resumo cai na descricao — ja exibida acima. */}
          {(selecionada.parcelas || []).length > 0 || !selecionada.descricao
            ? `${resumirParcelasCondicao(selecionada)} · `
            : ""}
          {selecionada.contagem_dias === "UTEIS" ? "dias úteis" : "dias corridos"}
          {taxaAtual != null ? ` · taxa aplicada no motor: ${Number(taxaAtual)}%` : ""}
        </p>
      )}
      {!selecionada && (
        <p className="hint">
          Boleto e dinheiro têm taxa zero — "sem condição" é escolha válida.
          A taxa entra como s2 no divisor; as parcelas viram o cronograma de
          recebimento do pedido.
        </p>
      )}
      {congelado && (
        <p className="hint">
          Preço congelado no envio ao cliente — para trocar a forma de
          pagamento, crie uma nova versão.
        </p>
      )}
    </div>
  );
}
